import { memo } from 'react';
import { Modal, useTheme } from '@tw/tw-component-library';
import { FaTrash, FaSpinner } from 'react-icons/fa6';
import styled, { keyframes } from 'styled-components';

// ============= Styled Components =============
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerIcon = styled(FaSpinner)`
  animation: ${spin} 1s linear infinite;
`;

const ActionButton = styled.button<{
  variant?: 'primary' | 'danger';
  isLoading?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ theme, variant }) =>
    variant === 'danger' ? theme.colors.error : theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.onDark ?? theme.colors.background};

  &:hover:not(:disabled) {
    background-color: ${({ theme, variant }) =>
      variant === 'danger' ? theme.colors.error : theme.colors.secondary};
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

// ============= Types =============
interface CancelWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isCancelling: boolean;
}

// ============= Component =============
/**
 * CancelWorkflowModal Component
 * Modal for confirming workflow cancellation
 */
export const CancelWorkflowModal = memo<CancelWorkflowModalProps>(
  ({ isOpen, onClose, onConfirm, isCancelling }) => {
    const { theme } = useTheme();

    return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm Cancel"
      size="small"
    >
      <div style={{ padding: '1rem 0' }}>
        <p
          style={{
            marginBottom: '1rem',
            fontSize: '1rem',
            color: theme.colors.text.primary,
          }}
        >
          Are you sure you want to cancel this workflow?
        </p>
        <p
          style={{
            marginBottom: '2rem',
            fontSize: '0.9rem',
            color: theme.colors.text.secondary,
          }}
        >
          This action cannot be undone and will stop the workflow process.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
          }}
        >
          <ActionButton
            onClick={onClose}
            style={{ backgroundColor: theme.colors.grays.gray1 }}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="danger"
            onClick={onConfirm}
            isLoading={isCancelling}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <SpinnerIcon />
                Cancelling...
              </>
            ) : (
              <>
                <FaTrash />
                Cancel Workflow
              </>
            )}
          </ActionButton>
        </div>
      </div>
    </Modal>
    );
  }
);

CancelWorkflowModal.displayName = 'CancelWorkflowModal';
